<template>
  <transition name="tk-notif">
    <div
      v-if="notification"
      class="tk-notif"
      :class="globalTheme ? 'theme-dark' : 'theme-light'"
      role="alert"
      @click="goToTicket"
    >
      <div class="tk-notif__icon" :class="`tk-notif__icon--${notification.type}`">
        <v-icon size="18" color="white">{{ icon }}</v-icon>
      </div>

      <div class="tk-notif__body">
        <div class="tk-notif__title">{{ notification.title }}</div>
        <div class="tk-notif__message">{{ notification.message }}</div>
        <div class="tk-notif__link">Ver ticket #{{ notification.ticketId }} →</div>
      </div>

      <button class="tk-notif__close" title="Cerrar" @click.stop="close">
        <v-icon size="16">mdi-close</v-icon>
      </button>
    </div>
  </transition>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

// Más largo que el toast genérico (3-4s, ver store/app/actions.js): esta
// notificación tiene más texto y un link, necesita más tiempo para leerse.
const AUTO_DISMISS_MS = 8000

export default {
  name: 'TicketRealtimeNotification',

  data: () => ({
    dismissTimer: null,
  }),

  computed: {
    ...mapState('app', ['globalTheme']),
    ...mapState('ticketsRealtime', ['activeNotification']),

    notification() {
      return this.activeNotification
    },

    icon() {
      return this.notification?.type === 'comentario'
        ? 'mdi-comment-text-outline'
        : 'mdi-lock-open-variant-outline'
    },
  },

  watch: {
    activeNotification(val) {
      clearTimeout(this.dismissTimer)
      if (val) this.dismissTimer = setTimeout(() => this.close(), AUTO_DISMISS_MS)
    },
  },

  beforeDestroy() {
    clearTimeout(this.dismissTimer)
  },

  methods: {
    ...mapMutations('ticketsRealtime', ['CLEAR_ACTIVE_NOTIFICATION']),

    close() {
      this.CLEAR_ACTIVE_NOTIFICATION()
    },

    goToTicket() {
      if (!this.notification) return
      const id = this.notification.ticketId
      this.close()
      if (this.$route.name === 'ticket-detail' && String(this.$route.params.id) === String(id)) return
      this.$router.push({ name: 'ticket-detail', params: { id } }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.tk-notif {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 400;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 340px;
  max-width: calc(100vw - 32px);
  padding: 14px 16px;
  border-radius: 14px;
  background: var(--surface, #131923);
  border: 1px solid var(--border, #1f2632);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  cursor: pointer;
  font-family: var(--font-sans, inherit);
}

.tk-notif__icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tk-notif__icon--comentario { background: var(--brand, #0096c7); }
.tk-notif__icon--reserva-quitada { background: var(--warning, #c47900); }

.tk-notif__body {
  flex: 1;
  min-width: 0;
}

.tk-notif__title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text, #e8eaef);
  margin-bottom: 2px;
}

.tk-notif__message {
  font-size: 13px;
  color: var(--text-2, #a4abb7);
  line-height: 1.35;
}

.tk-notif__link {
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--link, #4fbde0);
}

.tk-notif__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: var(--text-3, #6d7585);
  background: transparent;
  transition: background 120ms ease;
}
.tk-notif__close:hover {
  background: var(--hover-strong, rgba(255, 255, 255, 0.08));
}

.tk-notif-enter-active,
.tk-notif-leave-active {
  transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease;
}
.tk-notif-enter,
.tk-notif-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

@media (max-width: 480px) {
  .tk-notif {
    right: 12px;
    left: 12px;
    bottom: 12px;
    width: auto;
  }
}
</style>
